/**
 * Funnel counters for the Overview, the Pipeline and Alert Flow, counted from records already read.
 * Pure: it takes the rings as arrays, so any data source (and the query cache) can feed it, and the
 * same rings are not downloaded again to count them. Moved out of `api-utils/counters`; the only
 * change is that a campaign now needs two or more incidents (item 3).
 */
import { resolveCandidatePipelineBucket } from '../../api-utils/threats';
import { isDuplicateEvent } from '../../api-utils/security_events';
import { isSourceActive } from '../../api-utils/sources';
import {
  clockStatFromDurations,
  emptyFunnelCounters,
  isEscalatedPlan,
  isWithinFunnelWindow,
  normalizeRiskBand,
  remediationTime,
  sourceRefId,
  DEFAULT_FUNNEL_WINDOW,
  type FunnelCounters,
  type FunnelWindow,
} from '../../lib/funnel';
import { groupCampaigns } from './campaigns';
import type {
  RemediationItem,
  SecurityEventItem,
  SourceItem,
  ThreatElementDocument,
  ThreatEventCandidateItem,
  ThreatEventItem,
} from '../domain/types';

/** The rings the counters are counted from. */
export interface FunnelInputs {
  candidates: ThreatEventCandidateItem[];
  events: SecurityEventItem[];
  threatEvents: ThreatEventItem[];
  elements: ThreatElementDocument[];
  remediations: RemediationItem[];
  sources: SourceItem[];
}

function candidateTime(candidate: {
  detected_at?: string;
  processed_at?: string;
  analysis_started_at?: string;
}): string {
  return candidate.detected_at || candidate.processed_at || candidate.analysis_started_at || '';
}

function eventTime(event: { event_time?: string; ingestion_time?: string }): string {
  return event.event_time || event.ingestion_time || '';
}

function threatTime(event: { created_at?: string; first_event_time?: string }): string {
  return event.created_at || event.first_event_time || '';
}

function remMode(rem: RemediationItem): 'auto' | 'gated' | '' {
  const mode = String(rem.execution_mode || '').toLowerCase().trim();
  if (mode === 'auto') return 'auto';
  if (mode === 'gated') return 'gated';
  return '';
}

function parseMs(iso?: string): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function durationMs(startIso?: string, endIso?: string): number | null {
  const start = parseMs(startIso);
  const end = parseMs(endIso);
  if (start == null || end == null || end < start) return null;
  return end - start;
}

function isOpenThreatStatus(status: string): boolean {
  return status === 'active' || status === 'in_progress' || status === 'reopened' || status === '';
}

function isHumanDecision(rem: RemediationItem): boolean {
  if (remMode(rem) !== 'gated') return false;
  const status = String(rem.status || '').toLowerCase();
  return (
    status === 'pending' ||
    status === 'confirmed' ||
    status === 'completed' ||
    status === 'reverted' ||
    status === 'in_progress'
  );
}

export function computeFunnelCounters(
  inputs: FunnelInputs,
  nowMs = Date.now(),
  window: FunnelWindow = DEFAULT_FUNNEL_WINDOW
): FunnelCounters {
  const { candidates, events, threatEvents, elements, remediations, sources } = inputs;

  const counters = emptyFunnelCounters(new Date(nowMs).toISOString(), window);

  const windowedCandidates = candidates.filter((c) =>
    isWithinFunnelWindow(candidateTime(c), nowMs, window)
  );
  counters.candidates.registered = windowedCandidates.length;
  for (const candidate of windowedCandidates) {
    const bucket = resolveCandidatePipelineBucket(candidate);
    if (bucket === 'pending') counters.candidates.pending += 1;
    else if (bucket === 'analyzing') counters.candidates.analyzing += 1;
    else if (bucket === 'duplicated') counters.candidates.duplicated += 1;
    else if (bucket === 'not_threat') {
      counters.candidates.unique += 1;
      counters.verdicts.baseline += 1;
    } else if (bucket === 'false_positive') {
      counters.candidates.unique += 1;
      counters.verdicts.suppressed += 1;
    } else if (bucket === 'escalated') {
      counters.candidates.unique += 1;
      counters.verdicts.escalated += 1;
    } else if (bucket === 'threat') {
      counters.candidates.unique += 1;
      counters.verdicts.threat += 1;
    } else {
      counters.candidates.error += 1;
    }
  }

  const windowedEvents = events.filter((e) => isWithinFunnelWindow(eventTime(e), nowMs, window));
  counters.events.total = windowedEvents.length;
  for (const event of windowedEvents) {
    if (isDuplicateEvent(event.is_duplicate)) counters.events.duplicate += 1;
    else counters.events.new += 1;
  }

  const actions = elements.filter((el) => String(el.element_type || '').toLowerCase() === 'action');
  // A campaign is a group of two or more incidents (item 3), the same rule the feed groups by.
  const campaigns = groupCampaigns(elements, threatEvents);
  const memberActionIds = new Set<string>();
  for (const group of campaigns) {
    group.actions.forEach((action) => memberActionIds.add(action._id || ''));
  }

  const windowedThreats = threatEvents.filter((t) => isWithinFunnelWindow(threatTime(t), nowMs, window));
  counters.threats.total = windowedThreats.length;
  const actionByThreatId = new Map<string, string>();
  for (const action of actions) {
    const tid = sourceRefId(action.threat_event_id);
    if (tid && action._id) actionByThreatId.set(tid, action._id);
  }

  for (const threat of windowedThreats) {
    const status = String(threat.detection_status || 'active').toLowerCase();
    if (status === 'folded') counters.threats.folded += 1;
    else if (status === 'in_progress') counters.threats.inProgress += 1;
    else if (status === 'remediated') counters.threats.remediated += 1;
    else counters.threats.active += 1;

    const actionId = actionByThreatId.get(threat._id);
    if (actionId && memberActionIds.has(actionId)) counters.threats.inCampaign += 1;
    else counters.threats.standalone += 1;
  }

  // Point-in-time open incidents + severity (Active Incidents tile).
  for (const threat of threatEvents) {
    const status = String(threat.detection_status || 'active').toLowerCase();
    if (!isOpenThreatStatus(status) || status === 'folded') continue;
    if (status === 'error') continue;
    counters.threats.open += 1;
    counters.threats.byRisk[normalizeRiskBand(threat.detection_risk_level)] += 1;
  }

  counters.campaigns.total = campaigns.length;
  for (const { campaign } of campaigns) {
    const status = String(campaign.status || '').toLowerCase();
    if (status === 'closed' || status === 'dismissed') counters.campaigns.closed += 1;
    else counters.campaigns.open += 1;
  }

  const windowedRemediations = remediations.filter((rem) =>
    isWithinFunnelWindow(remediationTime(rem), nowMs, window)
  );

  counters.actions.total = remediations.length;
  for (const rem of remediations) {
    const status = String(rem.status || '').toLowerCase();
    if (status === 'pending') counters.actions.pending += 1;
    else if (status === 'confirmed') counters.actions.confirmed += 1;
    else if (status === 'in_progress') counters.actions.inProgress += 1;
    else if (status === 'completed') counters.actions.completed += 1;
    else if (status === 'cancelled') counters.actions.cancelled += 1;
    else if (status === 'reverted') counters.actions.reverted += 1;

    const mode = remMode(rem);
    if (mode === 'auto') counters.actions.auto += 1;
    else if (mode === 'gated') counters.actions.gated += 1;
  }

  // My Queue (point-in-time): gated awaiting decision.
  let oldestWait: number | null = null;
  for (const rem of remediations) {
    if (remMode(rem) !== 'gated') continue;
    const status = String(rem.status || '').toLowerCase();
    if (status !== 'pending' && status !== 'confirmed') continue;
    // One handed up to higher management no longer waits on this person.
    if (isEscalatedPlan(rem)) continue;
    counters.queue.waiting += 1;
    const assigned = parseMs(String(rem.assigned_at || rem.formed_at || ''));
    if (assigned != null) {
      const age = nowMs - assigned;
      if (age >= 0 && (oldestWait == null || age > oldestWait)) oldestWait = age;
    }
  }
  counters.queue.oldestWaitMs = oldestWait;

  // Compression / FP / auto-res / rollback (windowed).
  counters.compression.alerts = counters.events.total;
  counters.compression.incidents = counters.threats.total;
  counters.compression.decisions = windowedRemediations.filter(isHumanDecision).length;

  counters.fp.suppressed = counters.verdicts.suppressed;
  counters.fp.anomalies =
    counters.verdicts.suppressed + counters.verdicts.threat + counters.verdicts.escalated;
  counters.fp.threats = counters.verdicts.threat;

  for (const rem of windowedRemediations) {
    const status = String(rem.status || '').toLowerCase();
    const mode = remMode(rem);
    if (status === 'completed' || status === 'reverted') {
      counters.autoResolution.closed += 1;
      counters.rollback.executed += 1;
      if (mode === 'auto' && status === 'completed') counters.autoResolution.autoCompleted += 1;
      if (mode === 'gated' && status === 'completed') counters.autoResolution.gatedCompleted += 1;
    }
    if (status === 'reverted') {
      counters.rollback.reverted += 1;
      const cause = String(rem.revert_cause || '').toLowerCase();
      if (cause === 'validation_failed') counters.rollback.validationFailed += 1;
      else if (cause === 'human_rollback') counters.rollback.humanRollback += 1;
    }
  }

  // Clocks from remediations (+ threat first_event for resolve).
  const threatById = new Map(threatEvents.map((t) => [t._id, t]));
  const assignDurations: number[] = [];
  const triageDurations: number[] = [];
  const remediateDurations: number[] = [];
  const resolveDurations: number[] = [];

  for (const rem of windowedRemediations) {
    const status = String(rem.status || '').toLowerCase();
    if (status !== 'completed' && status !== 'reverted') continue;

    const formed = String(rem.formed_at || '');
    const assigned = String(rem.assigned_at || '');
    const decided = String(rem.decided_at || '');
    const verified = String(rem.verified_at || '');

    const assign = durationMs(formed, assigned);
    if (assign != null) assignDurations.push(assign);

    if (remMode(rem) === 'gated') {
      const triage = durationMs(assigned, decided);
      if (triage != null) triageDurations.push(triage);
    }

    const remediate = durationMs(decided, verified);
    if (remediate != null) remediateDurations.push(remediate);

    const threat = threatById.get(sourceRefId(rem.threat_event_id));
    const firstAlert = String(threat?.first_event_time || threat?.created_at || formed);
    const resolve = durationMs(firstAlert, verified);
    if (resolve != null) resolveDurations.push(resolve);
  }

  counters.clocks.assign = clockStatFromDurations(assignDurations);
  counters.clocks.triage = clockStatFromDurations(triageDurations);
  counters.clocks.remediate = clockStatFromDurations(remediateDurations);
  counters.clocks.resolve = clockStatFromDurations(resolveDurations);

  // Integration health (point-in-time).
  const connected = sources.filter(isSourceActive);
  counters.integrations.connected = connected.length;
  let healthy = 0;
  let worstLag: number | null = null;
  for (const source of connected) {
    const hasError = Boolean(String(source.last_error || '').trim());
    const scanned = parseMs(source.last_scanned_at);
    const lag = scanned != null ? Math.max(0, nowMs - scanned) : null;
    if (lag != null && (worstLag == null || lag > worstLag)) worstLag = lag;
    // Without cadence (D7), treat active + no error as healthy.
    if (!hasError) healthy += 1;
  }
  counters.integrations.healthy = healthy;
  counters.integrations.worstLagMs = worstLag;

  return counters;
}
